import './MainPageFacade.scss'
import React from "react";
import {observer} from "mobx-react-lite";
import {useNavigate} from "react-router";
import {Box, Button, Chip, Grid, Typography} from "@mui/material";
import {CONTENT_PADDING} from "../drawer/DrawerConstants.ts";
import {LoadingBarComponent} from "../../loading-bar/LoadingBarComponent.tsx";
import {Tag} from "~/components/tag/Tag.tsx";
import type {PageHeaderBack} from "~/components/main/header/PageHeader.tsx";
import {PageHeader} from "~/components/main/header/PageHeader.tsx";

type ButtonSpec = {
    type: 'btn'
    link: string
    label: string
} | {
    type: 'component'
    node: React.ReactNode
}

type Props = {
    title: string | React.ReactNode;
    titlePostfix?: React.ReactNode;
    titleTag?: React.ReactNode | string;
    /** Shown as a help icon after the title in the header */
    helpContent?: React.ReactNode;
    back?: string | false | ButtonSpec;
    backLabel?: string;
    backClass?: string;
    onBack?: () => void;
    next?: ButtonSpec;
    nextClass?: string;
    actionBarStart?: React.ReactNode;
    actionBarEnd?: React.ReactNode;
    className?: string;
    children?: React.ReactNode;
}
export const MainPageFacade: React.FC<Props> = observer((
  {
      title,
      titlePostfix,
      titleTag,
      helpContent,
      back,
      backLabel,
      backClass,
      onBack,
      next,
      nextClass,
      actionBarStart,
      actionBarEnd,
      className,
      children,
  }) => {
  const navigate = useNavigate();

  const _titleTag = React.useMemo(() => {
    if (titleTag === undefined) return null;
    if (typeof titleTag === 'string') {
        if (titleTag === 'beta') {
            return <Chip label="BETA" variant="filled" color="primary"/>
        }
        if (titleTag === 'trial') {
        // TODO: How many of these different tags are we willing to support?
        //  We should align these tags with the Showcase/Experiment/Production to ensure there are confusions when going to production!
            return <Tag label="Trial" variant="filled" color="default" sx={{minHeight: '20px', height: '20px'}} />
        }
        return <Typography variant="subtitle1" component="span">{titleTag}</Typography>;
    }
    return titleTag;
  }, [titleTag]);

  // Back control and title live in the app's top bar (Mithra 3.1 header)
  const headerBack: PageHeaderBack | undefined = typeof back === 'string'
      ? {label: backLabel === undefined ? 'Back' : backLabel, onClick: () => onBack ? onBack() : navigate('' + back)}
      : (typeof back === 'object' && back.type === 'btn')
          ? {label: back.label === undefined ? 'Next' : back.label, onClick: () => navigate('' + back.link)}
          : undefined;
  const backNode = typeof back === 'object' && back.type === 'component' ? back.node : null;
  const hasActionBar = Boolean(backNode || actionBarStart || actionBarEnd || next);

  return <>
      <LoadingBarComponent/>
      <PageHeader
          title={<>{title}{titlePostfix}</>}
          back={headerBack}
          tag={_titleTag}
          helpContent={helpContent}
      />
      <main id="main-component-content" style={{
          padding: `${CONTENT_PADDING}px ${CONTENT_PADDING}px 0`
      }}>
          <div id="main-page-facade" className={className}>
              {hasActionBar &&
              <Grid sx={{marginBottom: '16px'}} container component="header" id="main-page-facade-header"
                    alignItems="center">
                  {backNode && <Grid className={backClass ?? 'text-center'}>{backNode}</Grid>}
                  {actionBarStart}
                  <Box sx={{flexGrow: 1}}/>
                  {actionBarEnd}
                  {next &&
            <Grid className={nextClass ?? 'text-center'}>
                {next?.type === 'btn' &&
                <Button
                  color="primary"
                  variant="contained"
                  // startIcon={<ArrowBack/>}
                  onClick={() => navigate('' + next.link)}>
                    {next.label === undefined ? 'Next' : next.label}
                </Button>
                }
                {next?.type === 'component' && next.node}
            </Grid>
                  }
              </Grid>}
              {children}
          </div>
      </main>
  </>
});
